import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';
import { modalCloseButton } from '../../../theme/modalClose';

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const BORDER = 'var(--hd-border)';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* Same overlay as the welcome video: foreground/30 with a soft blur. */
export const ModalOverlay = styled.div`
  ${brandTheme}
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  animation: ${fadeIn} 0.15s ease-out;

  .backdrop {
    position: absolute;
    inset: 0;
    background: color-mix(in oklab, var(--hd-foreground) 30%, transparent);
    backdrop-filter: blur(4px);
  }
`;

export const ModalDialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  padding: 28px;
  border: 1px solid ${BORDER};
  border-radius: 26px;
  text-align: center;
  color: ${FG};
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-elevated);
  animation: ${popIn} 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  .close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    color: ${MUTED_FG};
    background: color-mix(in oklab, var(--hd-background) 80%, transparent);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: ${FG};
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  /* Icon tile: a soft destructive wash to warn, the brand gradient once done. */
  .tile {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    margin: 4px auto 0;
    border-radius: 22px;

    svg {
      width: 24px;
      height: 24px;
    }

    &.warn {
      color: var(--hd-destructive);
      background: color-mix(in oklab, var(--hd-destructive) 10%, transparent);
      box-shadow: 0 0 0 6px color-mix(in oklab, var(--hd-destructive) 5%, transparent);
    }

    &.done {
      color: #fff;
      background: var(--hd-gradient-houdiny);
      box-shadow: var(--hd-shadow-card);
    }
  }

  && h2 {
    margin: 20px 0 0;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  && p {
    max-width: 340px;
    margin: 8px auto 0;
    font-size: 14px;
    line-height: 1.625;
    color: ${MUTED_FG};
  }

  .actions {
    display: flex;
    gap: 10px;
    margin-top: 24px;

    button {
      display: inline-flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      gap: 8px;
      height: 44px;
      padding: 0 16px;
      border-radius: 18px;
      font-size: 14px;
      cursor: pointer;
      transition:
        background 0.15s ease,
        transform 0.15s ease,
        opacity 0.15s ease;

      &:disabled {
        cursor: default;
        opacity: 0.7;
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .secondary {
      border: 1px solid ${BORDER};
      font-weight: 500;
      color: ${FG};
      background: var(--hd-card);

      &:hover:not(:disabled) {
        background: var(--hd-muted);
      }
    }

    .destructive {
      border: none;
      font-weight: 600;
      color: var(--hd-destructive-foreground);
      background: var(--hd-destructive);
      box-shadow: var(--hd-shadow-card);

      &:hover:not(:disabled) {
        background: color-mix(in oklab, var(--hd-destructive) 90%, black);
      }
    }

    .primary {
      border: none;
      font-weight: 600;
      color: #fff;
      background: var(--hd-gradient-houdiny);
      box-shadow: var(--hd-shadow-card);

      &:hover:not(:disabled) {
        transform: scale(1.01);
      }
    }

    .spin {
      animation: ${spin} 1s linear infinite;
    }
  }
  /* Same close button as every other modal (theme/modalClose). */
  .close {
    ${modalCloseButton}
  }

`;
